// Typed port of the vanilla DB.loadAppState() / DB.saveAppState().
//
// Wire format (vanilla schema v2 — verified 2026-04-18 against live data):
//   users/{uid}/data/appstate_meta       — native scalar fields + opts
//                                          (adds `_schemaVersion: 2`, `version: "3.4"`)
//   users/{uid}/data/appstate_clients    — { json: "<JSON array string>" }
//   users/{uid}/data/appstate_projects   — { json: "<JSON array string>" }
//   users/{uid}/data/appstate_estimates  — { json: "<JSON array string>" }
//   users/{uid}/data/appstate_expenses   — { json: "<JSON array string>" }
//   users/{uid}/data/appstate_payments   — { json: "<JSON array string>" }
//   users/{uid}/data/appstate_schedules  — { json: "<JSON array string>" }
//   users/{uid}/data/appstate_misc       — { json: "<stringified MiscPayload>" }
//     where MiscPayload is one object with inline arrays (NOT per-key stringified):
//       { reminders: [...], changeOrders: [...], subs: [...], vendors: [...],
//         customTemplates: [...], assignments: [...], pendingAssignExpenses: [...],
//         activityLog: [...], bids: [...], events: [...] }
//
// `data` field was a v2-only mistake; re-read vanilla's wire format.

import { getDoc, setDoc, serverTimestamp, writeBatch } from 'firebase/firestore'
import { db, userDoc } from '../firebase/firestore'
import { parseSlice, stringifySlice } from './converters'
import { stampLocalSave } from './echoGuard'
import type {
  AppState, AppstateMetaDoc, AppstateMiscDoc, AppstateSliceDoc, MiscPayload,
} from '../../types/firestore'
import type {
  ActivityLogEntry, Assignment, Attachment, Bid, CalendarEvent, ChangeOrder,
  Client, CustomTemplate, Estimate, Expense, Opts, Payment,
  PendingAssignExpense, Project, Reminder, Schedule, Sub, Vendor,
} from '../../types/domain'

const DEFAULT_OPTS: Opts = {
  profitMarginEnabled: false,
  profitMarginPct: 0,
  dateFormat: 'MM/DD/YYYY',
}

export const SCHEMA_VERSION = '3.4'
export const SCHEMA_NUMERIC = 2

// ── v1→v2 opts migrations ──────────────────────────────────────────────────
// Runs on every load so existing v1 users coming into v2 keep their settings
// instead of silently reverting to defaults. Idempotent.
function migrateLegacyOpts(raw: Opts): Opts {
  const o: Opts = { ...raw }
  const r = raw as Record<string, unknown>

  // Alert key rename: alertUnpaidExp → alertUnpaidExpense (v1 line 4784).
  if (o.alertUnpaidExpense === undefined && r.alertUnpaidExp !== undefined) {
    o.alertUnpaidExpense = Boolean(r.alertUnpaidExp)
  }

  return o
}

// ── v1 _onb doc migration ──────────────────────────────────────────────────
// V1 stored walkthrough/setup-checklist state in a separate `_onb` doc; v2
// keeps these on opts. Copy keys forward once so existing users don't see
// the tour replay.
async function migrateOnboardingDoc(uid: string, opts: Opts): Promise<Opts> {
  const hasMigrated = (opts as Record<string, unknown>)._onbMigrated === true
  if (hasMigrated) return opts
  try {
    const snap = await getDoc(userDoc(uid, '_onb'))
    if (!snap.exists()) {
      return { ...opts, _onbMigrated: true } as Opts
    }
    const d = snap.data() as Record<string, unknown>
    const next: Opts = { ...opts, _onbMigrated: true } as Opts
    const n = next as Record<string, unknown>
    const copy = [
      'walkthroughSeenAt', 'walkthroughCompleted',
      'setupChecklistDismissed', 'setupChecklistCompletedAt', 'setupChecklistCompletedDismissed',
    ]
    for (const k of copy) {
      if (d[k] !== undefined && n[k] === undefined) n[k] = d[k]
    }
    if (n.onboardingChecklistDone === undefined && d.setupChecklistCompletedAt) {
      n.onboardingChecklistDone = true
    }
    return next
  } catch {
    return opts
  }
}

// ── Misc doc helpers ───────────────────────────────────────────────────────

function parseMiscDoc(data: unknown): MiscPayload {
  if (!data || typeof (data as AppstateMiscDoc).json !== 'string') return {}
  try {
    const parsed = JSON.parse((data as AppstateMiscDoc).json as string)
    return parsed && typeof parsed === 'object' ? parsed as MiscPayload : {}
  } catch {
    return {}
  }
}

function miscFromState(state: AppState): MiscPayload {
  return {
    reminders:             state.reminders,
    changeOrders:          state.changeOrders,
    subs:                  state.subs,
    vendors:               state.vendors,
    customTemplates:       state.customTemplates,
    assignments:           state.assignments,
    pendingAssignExpenses: state.pendingAssignExpenses,
    activityLog:           state.activityLog,
    bids:                  state.bids,
    events:                state.events,
  }
}

function serializeMisc(payload: MiscPayload): string {
  return JSON.stringify(payload)
}

// ── Load ───────────────────────────────────────────────────────────────────

export async function loadAppState(uid: string): Promise<AppState> {
  const [
    metaSnap, clientsSnap, projectsSnap, estimatesSnap, expensesSnap,
    paymentsSnap, schedulesSnap, miscSnap, attachmentsSnap,
  ] = await Promise.all([
    getDoc(userDoc(uid, 'appstate_meta')),
    getDoc(userDoc(uid, 'appstate_clients')),
    getDoc(userDoc(uid, 'appstate_projects')),
    getDoc(userDoc(uid, 'appstate_estimates')),
    getDoc(userDoc(uid, 'appstate_expenses')),
    getDoc(userDoc(uid, 'appstate_payments')),
    getDoc(userDoc(uid, 'appstate_schedules')),
    getDoc(userDoc(uid, 'appstate_misc')),
    getDoc(userDoc(uid, 'attachments')),
  ])

  const meta = (metaSnap.data() ?? {}) as AppstateMetaDoc

  const clients   = parseSlice<Client>(  (clientsSnap.data()   as AppstateSliceDoc | undefined)?.json)
  const projects  = parseSlice<Project>( (projectsSnap.data()  as AppstateSliceDoc | undefined)?.json)
  const estimates = parseSlice<Estimate>((estimatesSnap.data() as AppstateSliceDoc | undefined)?.json)
  const expenses  = parseSlice<Expense>( (expensesSnap.data()  as AppstateSliceDoc | undefined)?.json)
  const payments  = parseSlice<Payment>( (paymentsSnap.data()  as AppstateSliceDoc | undefined)?.json)
  const schedules = parseSlice<Schedule>((schedulesSnap.data() as AppstateSliceDoc | undefined)?.json)

  const misc = parseMiscDoc(miscSnap.data())
  const attachments = parseSlice<Attachment>((attachmentsSnap.data() as AppstateSliceDoc | undefined)?.json)

  const mergedOpts = migrateLegacyOpts({ ...DEFAULT_OPTS, ...(meta.opts ?? {}) })
  const opts = await migrateOnboardingDoc(uid, mergedOpts)

  return {
    clients, projects, estimates, expenses, payments, schedules,
    reminders:             Array.isArray(misc.reminders)             ? misc.reminders             : [] as Reminder[],
    changeOrders:          Array.isArray(misc.changeOrders)          ? misc.changeOrders          : [] as ChangeOrder[],
    subs:                  Array.isArray(misc.subs)                  ? misc.subs                  : [] as Sub[],
    vendors:               Array.isArray(misc.vendors)               ? misc.vendors               : [] as Vendor[],
    customTemplates:       Array.isArray(misc.customTemplates)       ? misc.customTemplates       : [] as CustomTemplate[],
    assignments:           Array.isArray(misc.assignments)           ? misc.assignments           : [] as Assignment[],
    pendingAssignExpenses: Array.isArray(misc.pendingAssignExpenses) ? misc.pendingAssignExpenses : [] as PendingAssignExpense[],
    activityLog:           Array.isArray(misc.activityLog)           ? misc.activityLog           : [] as ActivityLogEntry[],
    bids:                  Array.isArray(misc.bids)                  ? misc.bids                  : [] as Bid[],
    events:                Array.isArray(misc.events)                ? misc.events                : [] as CalendarEvent[],
    attachments,

    opts,
    ...(meta.activeOvProj !== undefined ? { activeOvProj: meta.activeOvProj } : {}),

    nextClientId:  meta.nextClientId  ?? 1,
    nextProjId:    meta.nextProjId    ?? 1,
    nextEstId:     meta.nextEstId     ?? 1,
    nextEstItemId: meta.nextEstItemId ?? 1,
    nextSubId:     meta.nextSubId     ?? 1,
    nextVendorId:  meta.nextVendorId  ?? 1,
    nextCoId:      meta.nextCoId      ?? 1,
    nextExpId:     meta.nextExpId     ?? 1,
    nextBidId:     meta.nextBidId     ?? 1,
    nextSchedId:   meta.nextSchedId   ?? 1,
    nextPayId:     meta.nextPayId     ?? 1,
    nextRemId:     meta.nextRemId     ?? 1,
    nextAttachId:  meta.nextAttachId  ?? 1,
    nextEventId:   meta.nextEventId   ?? 1,

    version: meta.version ?? SCHEMA_VERSION,
  }
}

// ── Save (whole state) ─────────────────────────────────────────────────────

export async function saveAppState(uid: string, state: AppState): Promise<void> {
  const batch = writeBatch(db)

  // Stamp every slice's echo guard BEFORE the batched write — same rationale
  // as saveSlice. saveAppState writes 8 docs in one batch; each one's
  // realtime listener needs the stamp to recognise its echo.
  const metaTs      = stampLocalSave('meta')
  const clientsTs   = stampLocalSave('clients')
  const projectsTs  = stampLocalSave('projects')
  const estimatesTs = stampLocalSave('estimates')
  const expensesTs  = stampLocalSave('expenses')
  const paymentsTs  = stampLocalSave('payments')
  const schedulesTs = stampLocalSave('schedules')
  const miscTs      = stampLocalSave('misc')

  const metaDoc: AppstateMetaDoc = {
    nextClientId:  state.nextClientId,
    nextProjId:    state.nextProjId,
    nextEstId:     state.nextEstId,
    nextEstItemId: state.nextEstItemId,
    nextSubId:     state.nextSubId,
    nextVendorId:  state.nextVendorId,
    nextCoId:      state.nextCoId,
    nextExpId:     state.nextExpId,
    nextBidId:     state.nextBidId,
    nextSchedId:   state.nextSchedId,
    nextPayId:     state.nextPayId,
    nextRemId:     state.nextRemId,
    nextAttachId:  state.nextAttachId,
    nextEventId:   state.nextEventId,
    opts:          state.opts,
    ...(state.activeOvProj !== undefined ? { activeOvProj: state.activeOvProj } : {}),
    version:        state.version ?? SCHEMA_VERSION,
    _schemaVersion: SCHEMA_NUMERIC,
    _updatedAt:     serverTimestamp(),
    _updatedAtMs:   metaTs,
  }
  batch.set(userDoc(uid, 'appstate_meta'), metaDoc)

  batch.set(userDoc(uid, 'appstate_clients'),   { json: stringifySlice(state.clients),   _updatedAt: serverTimestamp(), _updatedAtMs: clientsTs })
  batch.set(userDoc(uid, 'appstate_projects'),  { json: stringifySlice(state.projects),  _updatedAt: serverTimestamp(), _updatedAtMs: projectsTs })
  batch.set(userDoc(uid, 'appstate_estimates'), { json: stringifySlice(state.estimates), _updatedAt: serverTimestamp(), _updatedAtMs: estimatesTs })
  batch.set(userDoc(uid, 'appstate_expenses'),  { json: stringifySlice(state.expenses),  _updatedAt: serverTimestamp(), _updatedAtMs: expensesTs })
  batch.set(userDoc(uid, 'appstate_payments'),  { json: stringifySlice(state.payments),  _updatedAt: serverTimestamp(), _updatedAtMs: paymentsTs })
  batch.set(userDoc(uid, 'appstate_schedules'), { json: stringifySlice(state.schedules), _updatedAt: serverTimestamp(), _updatedAtMs: schedulesTs })

  batch.set(userDoc(uid, 'appstate_misc'), {
    json: serializeMisc(miscFromState(state)),
    _updatedAt: serverTimestamp(),
    _updatedAtMs: miscTs,
  })

  await batch.commit()
}

// ── Slice-only write (used by per-collection mutation hooks) ───────────────

export async function saveSlice<T>(
  uid: string,
  slice: 'clients' | 'projects' | 'estimates' | 'expenses' | 'payments' | 'schedules',
  data: T[],
): Promise<void> {
  const docName = `appstate_${slice}` as const
  // Stamp the echo guard BEFORE the write so the realtime listener can
  // skip the round-trip echo (see echoGuard.ts). Without this stamp +
  // _updatedAtMs pair, the listener parses and reapplies every self-write
  // — which in a race against a later optimistic update can clobber the
  // fresher cache with a stale snapshot.
  const ts = stampLocalSave(slice)
  await setDoc(userDoc(uid, docName), {
    json: stringifySlice(data),
    _updatedAt: serverTimestamp(),
    _updatedAtMs: ts,
  })
}

// ── Misc-slice-only write ──────────────────────────────────────────────────
// Read current misc, merge caller's patch, write back as a single `json`
// blob. Keeps the existing caller API (`saveMisc(uid, { subs: arr })`)
// working with the new wire format.

export async function saveMisc(uid: string, patch: Partial<MiscPayload>): Promise<void> {
  const ref = userDoc(uid, 'appstate_misc')
  const snap = await getDoc(ref)
  const current = parseMiscDoc(snap.data())
  const next: MiscPayload = { ...current, ...patch }
  const ts = stampLocalSave('misc')
  await setDoc(ref, {
    json: serializeMisc(next),
    _updatedAt: serverTimestamp(),
    _updatedAtMs: ts,
  })
}

// ── Opts-only write ────────────────────────────────────────────────────────
// Replaces the whole `opts` object on the meta doc but leaves counters intact
// via merge. Options page uses this so a typo in one setting can't clobber
// nextProjId and friends.
export async function saveOpts(uid: string, opts: Opts): Promise<void> {
  const ts = stampLocalSave('meta')
  await setDoc(userDoc(uid, 'appstate_meta'), { opts, _updatedAt: serverTimestamp(), _updatedAtMs: ts }, { merge: true })
}

// ── Attachments (separate doc — see vanilla DB.saveAttachments) ────────────

export async function loadAttachments(uid: string): Promise<Attachment[]> {
  const snap = await getDoc(userDoc(uid, 'attachments'))
  const d = snap.data() as { json?: string } | undefined
  return parseSlice<Attachment>(d?.json)
}

export async function saveAttachments(uid: string, arr: Attachment[]): Promise<void> {
  const ts = stampLocalSave('attachments')
  await setDoc(userDoc(uid, 'attachments'), {
    json: stringifySlice(arr),
    _updatedAt: serverTimestamp(),
    _updatedAtMs: ts,
  })
}
